import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { UserProfileFormProps } from '@/custom-types'
import { useErrorHandlerContext, useOpeningElementsControllerContext, useUserLanguage } from '@/customHooks'
import { ProfileEditFormControlled } from './ProfileEditFormControlled'
import { useGetManagerQuery, useUpdateMangerMutation } from '@/graphql/generated'
import { Col, Row } from 'react-grid-system'
import { Button, LoadingSpinner, RowSpacer } from '../atoms'
import textsData from '@/textData/UserProfileTexts.json'
import { capitalizeFirstLetter } from '@/helpers'

const FormWrapper = styled.div`
  background-color: white;
  border-radius: ${({ theme }) => theme.border.radius.md};
  padding: 2rem;
  box-shadow: 0 8px 10px rgba(0, 0, 0, 0.1);
`

export const UserProfileEditForm = () => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en
  
  const [userProfileFormData, setUserProfileFormData] = useState<UserProfileFormProps | null>(null)

  const { confirmDialogModalOpen } = useOpeningElementsControllerContext()
  const { open: errorHandle } = useErrorHandlerContext()
  
  const {data, loading: getManagerQueryLoading, error, refetch} = useGetManagerQuery()

  const [updateManager, { loading: updateManagerIsLoading }] = useUpdateMangerMutation()

  const onChangeHandle = useCallback((profileData: UserProfileFormProps) => {
    setUserProfileFormData(profileData)
  }, [])

  const onConfirmHandle = () => {
    if (!data) return
    if (!userProfileFormData) return

    updateManager({ variables: {
      firstName: data.manager.firstName !== userProfileFormData.firstName ? userProfileFormData.firstName : undefined,
      lastName: data.manager.lastName !== userProfileFormData.lastName ? userProfileFormData.lastName : undefined,
      phoneNumber: data.manager.phoneNumber !== userProfileFormData.phoneNumber ? userProfileFormData.phoneNumber : undefined,
      email: data.manager.user.email !== userProfileFormData.email ? userProfileFormData.email : undefined,
    }})
      .then((response) => {
        confirmDialogModalOpen({
          title: texts.editProfile.confirmationDialog.title,
          description: `${capitalizeFirstLetter(response.data?.updateManager.firstName ?? '')} ${capitalizeFirstLetter(response.data?.updateManager.lastName ?? '')} ${texts.editProfile.confirmationDialog.description}`
        })
        refetch()
      })
      .catch(errorHandle)
  }

  useEffect(() => {
    if (!data) return
    setUserProfileFormData({
      firstName: data.manager.firstName,
      lastName: data.manager.lastName,
      phoneNumber: data.manager.phoneNumber,
      email: data.manager.user.email,
    })
  }, [data])

  if (getManagerQueryLoading) {
    return (
      <Row justify="center">
        <Col xs="content">
          <LoadingSpinner />
          <p>Loading...</p>
        </Col>
      </Row>
    )
  }

  if (error) errorHandle(error)
  return (
    <>
      <RowSpacer height={3} />
      <FormWrapper>
        <ProfileEditFormControlled
          onChange={onChangeHandle}
          defaultProfileData={userProfileFormData}
        />
        <RowSpacer height={2} />
        <Row>
          <Col>
            <Button
              onClick={onConfirmHandle}
              isLoading={updateManagerIsLoading}
            >
              {texts.buttons.confirm}
            </Button>
          </Col>
        </Row>
      </FormWrapper>
    </>
  )
}
